import React from "react";
import { sendContactRequest } from "../api/contact";
import { logEvent } from "../utils/analytics";

type FormState = {
  name: string;
  email: string;
  message: string;
  budgetRange: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = { name: "", email: "", message: "", budgetRange: "" };

export default function ContactForm() {
  const [state, setState] = React.useState<FormState>(initialState);
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [status, setStatus] = React.useState<"idle" | "sending" | "success" | "error">("idle");
  const [feedback, setFeedback] = React.useState("");

  function update(field: keyof FormState, value: string) {
    setState((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  const validate = (): FormErrors => {
    const nextErrors: FormErrors = {};
    if (!state.name.trim()) {
      nextErrors.name = "Name is required.";
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!state.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!emailPattern.test(state.email.trim())) {
      nextErrors.email = "Enter a valid email.";
    }
    if (!state.message.trim()) {
      nextErrors.message = "Message is required.";
    } else if (state.message.trim().length < 10) {
      nextErrors.message = "Share at least 10 characters.";
    }
    return nextErrors;
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setStatus("sending");
    setFeedback("");

    const result = await sendContactRequest(state);
    if (result.ok) {
      setStatus("success");
      setState(initialState);
      setFeedback("Message sent. Expect a reply within 24 hours.");
      logEvent("contact_submit", {
        name: state.name,
        email: state.email,
        hasMessage: state.message.trim().length > 0
      });
    } else {
      setStatus("error");
      setFeedback(result.message);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full bg-surface/85 border border-border-subtle rounded-card p-6 sm:p-8 shadow-card space-y-5"
    >
      <div>
        <label htmlFor="contact-name" className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
          Name
        </label>
        <input
          id="contact-name"
          value={state.name}
          onChange={(e) => update("name", e.target.value)}
          className={`mt-2 w-full rounded-lg border bg-transparent px-3 py-2 font-mono text-sm text-primary focus:outline-none focus:ring-2 focus:ring-accent ${
            errors.name ? "border-red-500/70" : "border-border-subtle/70"
          }`}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? "contact-name-error" : undefined}
        />
        {errors.name && (
          <p id="contact-name-error" className="mt-1 text-xs text-red-400">
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-email" className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          value={state.email}
          onChange={(e) => update("email", e.target.value)}
          className={`mt-2 w-full rounded-lg border bg-transparent px-3 py-2 font-mono text-sm text-primary focus:outline-none focus:ring-2 focus:ring-accent ${
            errors.email ? "border-red-500/70" : "border-border-subtle/70"
          }`}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? "contact-email-error" : undefined}
        />
        {errors.email && (
          <p id="contact-email-error" className="mt-1 text-xs text-red-400">
            {errors.email}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-message" className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
          Anything I should know about your current setup?
        </label>
        <textarea
          id="contact-message"
          value={state.message}
          onChange={(e) => update("message", e.target.value)}
          rows={6}
          className={`mt-2 w-full rounded-lg border bg-transparent px-3 py-2 font-mono text-sm text-primary focus:outline-none focus:ring-2 focus:ring-accent ${
            errors.message ? "border-red-500/70" : "border-border-subtle/70"
          }`}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? "contact-message-error" : undefined}
        />
        {errors.message && (
          <p id="contact-message-error" className="mt-1 text-xs text-red-400">
            {errors.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-budget" className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
          Budget range (optional)
        </label>
        <select
          id="contact-budget"
          value={state.budgetRange}
          onChange={(e) => update("budgetRange", e.target.value)}
          className="mt-2 w-full rounded-lg border bg-transparent px-3 py-2 font-mono text-sm text-black focus:outline-none focus:ring-2 focus:ring-accent border-border-subtle/70"
        >
          <option value="" disabled>
            Select a range
          </option>
          <option value="<2k">&lt; $2k</option>
          <option value="2-5k">$2k–$5k</option>
          <option value="5-10k">$5k–$10k</option>
          <option value="10k+">$10k+</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={status === "sending"}
        >
          {status === "sending" ? "Sending..." : "Send Message"}
        </button>
        <a href="mailto:contact@enkae.dev" className="text-sm text-text-subtle hover:text-accent transition">
          Or email contact@enkae.dev
        </a>
        <p className="mt-2 text-xs text-text-subtle basis-full">I usually reply within 24–48 hours.</p>
      </div>

      {feedback && (
        <p className={`text-sm ${status === "success" ? "text-green-400" : "text-red-400"}`}>{feedback}</p>
      )}
    </form>
  );
}

